import { useEffect, useState } from "react";
import { db } from "./firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

async function loadWaterLog(uid, date) {
  const snap = await getDoc(doc(db, "waterLogs", `${uid}_${date}`));
  return snap.exists() ? snap.data() : { glasses: 0 };
}

async function saveWaterLog(uid, date, glasses) {
  await setDoc(
    doc(db, "waterLogs", `${uid}_${date}`),
    { uid, date, glasses, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

async function loadWaterGoal(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? (snap.data().waterGoal ?? 8) : 8;
}

async function saveWaterGoal(uid, goal) {
  await setDoc(doc(db, "users", uid), { waterGoal: goal }, { merge: true });
}

export default function WaterTracker({ uid, date = todayStr() }) {
  const [glasses, setGlasses] = useState(0);
  const [goal, setGoal] = useState(8);
  const [editingGoal, setEditingGoal] = useState(false);
  const [draftGoal, setDraftGoal] = useState(8);
  const [saving, setSaving] = useState(false);

  // Load goal + today's log
  useEffect(() => {
    if (!uid) return;
    loadWaterGoal(uid).then((g) => {
      setGoal(g);
      setDraftGoal(g);
    });
  }, [uid]);

  useEffect(() => {
    if (!uid || !date) return;
    setGlasses(0);
    loadWaterLog(uid, date).then((data) => {
      setGlasses(data.glasses ?? 0);
    });
  }, [uid, date]);

  async function updateGlasses(delta) {
    const next = Math.max(0, glasses + delta);
    setGlasses(next);
    setSaving(true);
    try {
      await saveWaterLog(uid, date, next);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveGoal() {
    const cleaned = Math.max(1, parseInt(draftGoal, 10) || 8);
    setGoal(cleaned);
    setEditingGoal(false);
    await saveWaterGoal(uid, cleaned);
  }

  const pct = goal > 0 ? Math.min(100, (glasses / goal) * 100) : 0;
  const done = glasses >= goal && goal > 0;

  // Build glass icons (up to 12 shown, rest shown as number)
  const MAX_ICONS = 12;
  const showIcons = goal <= MAX_ICONS;

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>
          💧 Daily Water
          {done && <span style={{ marginLeft: "8px", fontSize: "14px" }}>✅</span>}
        </div>
        <button
          type="button"
          onClick={() => { setDraftGoal(goal); setEditingGoal(true); }}
          style={{
            padding: "4px 10px", borderRadius: "10px", border: "2px solid #bfdbfe",
            background: "#f5faff", color: "#4b6b8a", fontSize: "12px",
            fontWeight: "bold", cursor: "pointer",
          }}
        >
          Set Goal
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: "14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "6px" }}>
          <span style={{ color: "#1e3a5f", fontSize: "28px", fontWeight: "bold" }}>
            {glasses}
            <span style={{ fontSize: "14px", fontWeight: "normal", color: "#4b6b8a", marginLeft: "4px" }}>
              / {goal} glasses
            </span>
          </span>
          <span style={{ color: "#4b6b8a", fontSize: "13px" }}>{Math.round(pct)}%</span>
        </div>
        <div style={{ height: "10px", background: "#dbeafe", borderRadius: "999px", overflow: "hidden", border: "1px solid #bfdbfe" }}>
          <div style={{
            height: "100%",
            width: `${pct}%`,
            background: done
              ? "linear-gradient(90deg, #22c55e, #16a34a)"
              : "linear-gradient(90deg, #38bdf8, #2563eb)",
            borderRadius: "999px",
            transition: "width 0.3s ease",
          }} />
        </div>
      </div>

      {/* Glass icons (visual) */}
      {showIcons ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "14px" }}>
          {Array.from({ length: goal }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => updateGlasses(i < glasses ? -(glasses - i) : (i + 1) - glasses)}
              style={{
                width: "36px", height: "36px", borderRadius: "10px",
                border: i < glasses ? "2px solid #2563eb" : "2px solid #bfdbfe",
                background: i < glasses ? "#dbeafe" : "#f8fbff",
                fontSize: "18px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s",
              }}
              title={i < glasses ? "Tap to remove" : "Tap to add"}
            >
              {i < glasses ? "💧" : "🫙"}
            </button>
          ))}
        </div>
      ) : null}

      {/* +/- controls */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button
          type="button"
          onClick={() => updateGlasses(-1)}
          disabled={glasses === 0}
          style={{
            width: "44px", height: "44px", borderRadius: "999px",
            border: "2px solid #bfdbfe", background: "#f5faff",
            color: "#1e3a5f", fontWeight: "bold", fontSize: "22px",
            cursor: glasses === 0 ? "not-allowed" : "pointer",
            opacity: glasses === 0 ? 0.4 : 1,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >−</button>

        <div style={{ flex: 1, textAlign: "center", color: "#4b6b8a", fontSize: "13px" }}>
          {done
            ? "🎉 Goal reached!"
            : `${goal - glasses} more to go`}
        </div>

        <button
          type="button"
          onClick={() => updateGlasses(1)}
          style={{
            width: "44px", height: "44px", borderRadius: "999px",
            border: "2px solid #2563eb", background: "#e0f2fe",
            color: "#1e3a5f", fontWeight: "bold", fontSize: "22px",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >+</button>
      </div>

      {saving && (
        <div style={{ color: "#6b8cb0", fontSize: "11px", textAlign: "right", marginTop: "6px" }}>Saving…</div>
      )}

      {/* Edit Goal Modal */}
      {editingGoal && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(30,58,138,0.35)",
            zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
          onClick={() => setEditingGoal(false)}
        >
          <div
            style={{
              background: "#f5faff", borderRadius: "24px 24px 0 0", padding: "24px",
              width: "100%", maxWidth: "480px", boxSizing: "border-box",
              boxShadow: "0 -8px 32px rgba(30,58,138,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "18px", marginBottom: "4px" }}>
              Set Daily Water Goal
            </div>
            <p style={{ color: "#6b8cb0", fontSize: "13px", marginTop: 0, marginBottom: "18px" }}>
              How many glasses of water do you aim to drink each day?
            </p>

            <div style={{ display: "flex", alignItems: "center", gap: "16px", justifyContent: "center", marginBottom: "20px" }}>
              <button
                type="button"
                onClick={() => setDraftGoal((prev) => Math.max(1, (prev || 8) - 1))}
                style={{
                  width: "44px", height: "44px", borderRadius: "999px",
                  border: "2px solid #bfdbfe", background: "#f5faff",
                  color: "#1e3a5f", fontWeight: "bold", fontSize: "22px",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >−</button>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "48px", fontWeight: "bold", color: "#1e3a5f", lineHeight: 1 }}>
                  {draftGoal}
                </div>
                <div style={{ color: "#6b8cb0", fontSize: "13px", marginTop: "4px" }}>glasses / day</div>
              </div>
              <button
                type="button"
                onClick={() => setDraftGoal((prev) => (prev || 8) + 1)}
                style={{
                  width: "44px", height: "44px", borderRadius: "999px",
                  border: "2px solid #2563eb", background: "#e0f2fe",
                  color: "#1e3a5f", fontWeight: "bold", fontSize: "22px",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >+</button>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={handleSaveGoal}
                style={{
                  flex: 1, padding: "12px", borderRadius: "14px", border: "none",
                  background: "#2563eb", color: "#fff", fontWeight: "bold",
                  fontSize: "14px", cursor: "pointer",
                }}
              >
                Save Goal
              </button>
              <button
                type="button"
                onClick={() => setEditingGoal(false)}
                style={{
                  padding: "12px 16px", borderRadius: "14px",
                  border: "2px solid #bfdbfe", background: "#f5faff",
                  color: "#4b6b8a", fontWeight: "bold", fontSize: "14px", cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
